"use client";

// Business logic: Mobile menu component that handles navigation menu items and conditional user authentication buttons.
// Shows "mijn NSC" button for logged-in users or "Inloggen" button for guests.
// Manages menu state, scroll prevention, and responsive province navigation sections.

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import ChevronFill from "@/components/icons/ChevronFill";
import { cn } from "@/lib/utils";
import { ButtonLink } from "@/components/ui/button";
import { useStore } from "@/store";
import MobileProvinceMenuSection from "@/components/general/MobileProvinceMenuSection";

const websiteId = process.env.NEXT_PUBLIC_WEBSITE_ID as "nsc" | "jsc" || "nsc";

const MobileMenu = ({ open, menuData }: { open: boolean; menuData: any }) => {
  const { provinces } = useStore();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean | null>(null);

  // Check user authentication status
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch('/api/auth/check-user');
        const data = await response.json();
        setIsUserLoggedIn(!!data.user);
      } catch (error) {
        setIsUserLoggedIn(false);
      }
    };

    if (open) {
      checkAuthStatus();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [open]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      animate={{ opacity: open ? 1 : 0 }}
      style={{ pointerEvents: open ? "all" : "none" }}
      className={cn(
        "z-60 absolute h-auto max-h-[calc(100vh-113px)] w-full overflow-auto bg-nsc-dark-blue pb-4 pt-9 lg:hidden",
        websiteId === "jsc" && "bg-white"
      )}
    >
      <div className="container">
        <ul
          className={cn(
            "flex list-none flex-col gap-8 pl-0 text-white",
            websiteId === "jsc" && "text-black"
          )}
        >
          {menuData?.menu?.items.map((item: any) => {
            return item.type === "wrapper_element" ? (
              <NestedMenuItem key={item.id} item={item} provinces={provinces} />
            ) : (
              <li key={item.url} className="leading-0 font-oswald text-base font-bold uppercase">
                <Link href={item.url}>{item.title}</Link>
              </li>
            );
          })}

          <li className="leading-0 mt-0 font-oswald text-base font-bold uppercase">
            <ButtonLink variant="default" size="default" href="https://partijnieuwsociaalcontract.nl/lid-worden" className="w-32 h-10">
              Lid worden
            </ButtonLink>
          </li>

          {isUserLoggedIn !== null && (
            <li className="leading-0 -mt-2 font-oswald text-base font-bold uppercase">
              <ButtonLink 
                variant="default" 
                size="default" 
                href={isUserLoggedIn ? "/mijn-account" : "/mijn-account/inloggen"}
                className="w-32 h-10"
              >
                {isUserLoggedIn ? "mijn NSC" : "Inloggen"}
              </ButtonLink>
            </li>
          )}
        </ul>
      </div>
    </motion.div>
  );
};

const NestedMenuItem = ({ item, provinces }: { item: any; provinces?: any[] }) => {
  const [open, setOpen] = useState(false);

  return (
    <li className="leading-0 font-oswald text-base font-bold uppercase">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-2 uppercase text-white",
          websiteId === "jsc" && "text-black"
        )}
      >
        {item.title}
        <ChevronFill
          className={cn("ml-2 transition-transform", open ? "!rotate-0" : "!rotate-180")}
          color={websiteId === "jsc" ? "black" : "white"}
          direction="down"
        />
      </button>
      {open && (
        <ul className="flex flex-col gap-6 py-10 pl-10">
          {item.nestedItems.map((nestedItem: any) => {
            // Special handling for province menu items
            if (nestedItem.type === "province_wrapper") {
              return (
                <MobileProvinceMenuSection
                  key={nestedItem.id}
                  item={nestedItem}
                  provinces={provinces || []}
                />
              );
            }
            
            return (
              <li key={nestedItem.id} className="leading-0 font-oswald text-base font-bold uppercase">
                <Link href={nestedItem.url}>{nestedItem.title}</Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

export default MobileMenu;
